import { RowSelectionContext } from "@/components/data-table/context/RowSelectionContext";
import { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { useCallback, useContext, useRef, useSyncExternalStore } from "react";

const EMPTY_ROW_SELECTION: RowSelectionState = Object.freeze({});
const noopSubscribe = () => () => undefined;

function selectionToRowSelection(
  selections: ReadonlySet<string>,
): RowSelectionState {
  if (selections.size === 0) {
    return EMPTY_ROW_SELECTION;
  }
  return Object.fromEntries(
    [...selections].map((id) => [String(id), true] as const),
  ) as RowSelectionState;
}

function rowSelectionToIds(state: RowSelectionState): Set<string> {
  return new Set(
    Object.entries(state)
      .filter(([, selected]) => selected)
      .map(([id]) => id),
  );
}

export function useAutoSyncedRowSelection({ enabled }: { enabled: boolean }) {
  const selectionStore = useContext(RowSelectionContext)?.store;
  const shouldAutoSyncRowSelection = Boolean(enabled && selectionStore);
  const snapshotCacheRef = useRef<{
    revision: number;
    state: RowSelectionState;
  }>({
    revision: -1,
    state: EMPTY_ROW_SELECTION,
  });

  const subscribe =
    shouldAutoSyncRowSelection && selectionStore
      ? selectionStore.subscribe
      : noopSubscribe;

  const rowSelection = useSyncExternalStore(
    subscribe,
    () => {
      if (!shouldAutoSyncRowSelection || !selectionStore) {
        return EMPTY_ROW_SELECTION;
      }
      const revision = selectionStore.getRevision();
      if (snapshotCacheRef.current.revision === revision) {
        return snapshotCacheRef.current.state;
      }
      const state = selectionToRowSelection(selectionStore.getSelection());
      snapshotCacheRef.current = { revision, state };
      return state;
    },
    () => EMPTY_ROW_SELECTION,
  );

  const setRowSelection = useCallback<OnChangeFn<RowSelectionState>>(
    (updater) => {
      if (!shouldAutoSyncRowSelection || !selectionStore) {
        return;
      }
      const currentSelection = selectionStore.getSelection();
      const prev = selectionToRowSelection(currentSelection);
      const next = typeof updater === "function" ? updater(prev) : updater;
      const nextSelection = rowSelectionToIds(next);
      const idsToRemove = [...currentSelection].filter(
        (id) => !nextSelection.has(id),
      );
      const idsToAdd = [...nextSelection].filter(
        (id) => !currentSelection.has(id),
      );
      if (idsToRemove.length === 0 && idsToAdd.length === 0) {
        return;
      }
      if (idsToRemove.length > 0) {
        selectionStore.toggleMany(idsToRemove);
      }
      if (idsToAdd.length > 0) {
        selectionStore.toggleMany(idsToAdd);
      }
    },
    [selectionStore, shouldAutoSyncRowSelection],
  );

  return { rowSelection, setRowSelection };
}
